import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import en from "./en.json";
import zh from "./zh.json";
import es from "./es.json";

/** A text reference: a key with its parameters, resolved in the current language (the mock's Y()). */
export interface TextRef {
  k: string;
  p?: Record<string, number | string | TextRef>;
}
export type Text = string | TextRef;

export type Lang = "en" | "zh" | "es";
export const LANGS: Array<{ code: Lang; label: string; locale: string }> = [
  { code: "en", label: "EN", locale: "en-US" },
  { code: "zh", label: "中文", locale: "zh-CN" },
  { code: "es", label: "ES", locale: "es-ES" },
];
const TABLES: Record<Lang, Record<string, string>> = { en, zh, es };
const STORAGE_KEY = "woodpecker-lang";
const warned = new Set<string>();

export function Y(k: string, p?: TextRef["p"]): TextRef {
  return p ? { k, p } : { k };
}

function readLang(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "zh" || v === "es" ? v : "en";
  } catch {
    return "en";
  }
}

export function localeOf(lang: Lang): string {
  return LANGS.find((l) => l.code === lang)?.locale ?? "en-US";
}

export function formatNumber(lang: Lang, n: number): string {
  return n.toLocaleString(localeOf(lang), { useGrouping: true, maximumFractionDigits: 6 });
}

function fill(lang: Lang, text: string, p?: TextRef["p"]): string {
  if (!p) return text;
  return text.replace(/\{(\w+)\}/g, (m, name: string) => {
    if (!(name in p)) return m;
    const v = p[name]!;
    if (typeof v === "number") return formatNumber(lang, v);
    if (typeof v === "string") return v;
    return translate(lang, v.k, v.p);
  });
}

/** The text of a key in a language; an empty or missing slot falls back to English (D25). */
export function translate(lang: Lang, key: string, p?: TextRef["p"]): string {
  const local = TABLES[lang][key];
  if (local === undefined || local === "") {
    if (lang !== "en" && !warned.has(`${lang}:${key}`)) {
      warned.add(`${lang}:${key}`);
      console.warn(`[i18n] no ${lang} text for "${key}", English shown`);
    }
    const base = (en as Record<string, string>)[key];
    if (base === undefined) {
      if (!warned.has(key)) {
        warned.add(key);
        console.warn(`[i18n] unknown key "${key}"`);
      }
      return key;
    }
    return fill(lang, base, p);
  }
  return fill(lang, local, p);
}

export function resolveText(lang: Lang, t: Text): string {
  return typeof t === "string" ? t : translate(lang, t.k, t.p);
}

const LangContext = createContext<{ lang: Lang; setLang: (l: Lang) => void } | null>(null);

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(readLang);
  useEffect(() => {
    document.documentElement.lang = localeOf(lang);
    document.title = translate(lang, "shell.title");
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* no storage */
    }
  }, [lang]);
  const setLang = useCallback((l: Lang) => setLangState(l), []);
  const value = useMemo(() => ({ lang, setLang }), [lang, setLang]);
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang needs LangProvider");
  return ctx;
}

/** t(key, params): the text of a key. */
export function useT() {
  const { lang } = useLang();
  return useCallback((key: string, p?: TextRef["p"]) => translate(lang, key, p), [lang]);
}

/** tt(text): a string or a TextRef. */
export function useTT() {
  const { lang } = useLang();
  return useCallback((t: Text) => resolveText(lang, t), [lang]);
}

/** rich(key, { name: <node> }): the text with React nodes in the placeholders. */
export function useRich() {
  const { lang } = useLang();
  return useCallback(
    (key: string, parts: Record<string, ReactNode>): ReactNode[] => {
      const text = translate(lang, key);
      const out: ReactNode[] = [];
      const re = /\{(\w+)\}/g;
      let last = 0;
      let m: RegExpExecArray | null;
      while ((m = re.exec(text))) {
        if (m.index > last) out.push(text.slice(last, m.index));
        const name = m[1]!;
        out.push(<span key={m.index}>{name in parts ? parts[name] : m[0]}</span>);
        last = m.index + m[0].length;
      }
      if (last < text.length) out.push(text.slice(last));
      return out;
    },
    [lang],
  );
}

/** n(number): the number with the language's digit grouping. */
export function useN() {
  const { lang } = useLang();
  return useCallback((n: number) => formatNumber(lang, n), [lang]);
}
